import React, {useCallback, useEffect, useMemo, useState} from 'react';

import type {IconProp} from '@fortawesome/fontawesome-svg-core';
import {faCheck, faSpinner} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import {
  apiCreateFdFundingAsk,
  apiGetFdFundingAsksByApplicationId,
} from 'constants/Api';
import type {
  IFdApplicationType,
  IFdFundingAsk,
  IFdFundingRequestCost,
  IFdOperatingCost,
  IFdProductionCost,
} from 'constants/interfaces';
import {lshGetOperatingCostTotalFromMonthlyCosts} from './steps/financials/financials.utils';

interface FundingRequestOption {
  id: string;
  label: string;
  cost: number;
  source: 'core' | 'operating' | 'custom';
}

export interface ApplicationFinancialsFundingRequestController {
  canSubmit: boolean;
  isLoading: boolean;
  isSaving: boolean;
  save: () => Promise<void>;
}

interface ApplicationFinancialsFundingRequestStepProps {
  applicationId: number;
  application?: IFdApplicationType;
  onRegisterController: (
    controller: ApplicationFinancialsFundingRequestController,
  ) => void;
  onComplete: () => Promise<void> | void;
}

const MONTH_OPTIONS = [
  {value: '1', label: 'January'},
  {value: '2', label: 'February'},
  {value: '3', label: 'March'},
  {value: '4', label: 'April'},
  {value: '5', label: 'May'},
  {value: '6', label: 'June'},
  {value: '7', label: 'July'},
  {value: '8', label: 'August'},
  {value: '9', label: 'September'},
  {value: '10', label: 'October'},
  {value: '11', label: 'November'},
  {value: '12', label: 'December'},
];

const iconCheck = faCheck as IconProp;
const iconSpinner = faSpinner as IconProp;

function parseCurrency(value: string | number | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const raw = String(value ?? '').trim();
  if (!raw) return 0;

  const cleaned = raw.replace(/[^\d,.-]/g, '');
  if (!cleaned) return 0;

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  let normalized = cleaned;
  if (hasComma && hasDot) {
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    normalized =
      lastComma > lastDot
        ? cleaned.replace(/\./g, '').replace(',', '.')
        : cleaned.replace(/,/g, '');
  } else if (hasComma) {
    const parts = cleaned.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      normalized = `${parts[0]}.${parts[1]}`;
    } else {
      normalized = cleaned.replace(/,/g, '');
    }
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toCurrency(value: number) {
  return `R${new Intl.NumberFormat('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;
}

function buildCoreOptions(
  productionCosts: IFdProductionCost[] | undefined,
): FundingRequestOption[] {
  const totals = (productionCosts || []).reduce(
    (acc, item) => {
      acc.seed += Number(item.seed ?? 0);
      acc.fertiliser += Number(item.fertiliser ?? 0);
      acc.chemical += Number(item.chemical ?? 0);
      return acc;
    },
    {seed: 0, fertiliser: 0, chemical: 0},
  );

  const entries: FundingRequestOption[] = [
    {
      id: 'seed',
      label: 'Seed',
      cost: totals.seed,
      source: 'core',
    },
    {
      id: 'fertiliser',
      label: 'Fertiliser',
      cost: totals.fertiliser,
      source: 'core',
    },
    {
      id: 'chemical',
      label: 'Chemical',
      cost: totals.chemical,
      source: 'core',
    },
  ];

  return entries.filter((item) => item.cost > 0);
}

function parseOperatingCostTotal(record: IFdOperatingCost): number {
  return lshGetOperatingCostTotalFromMonthlyCosts(record.monthlyCosts);
}

function buildOperatingExpenseOptions(
  operatingCosts: IFdOperatingCost[] | undefined,
): FundingRequestOption[] {
  return (operatingCosts || [])
    .map((expense) => ({
      id: String(expense.id),
      label: String(expense.expense?.name || 'Operating expense'),
      cost: parseOperatingCostTotal(expense),
      source: 'operating' as const,
    }))
    .filter((item) => item.cost > 0);
}

function mergeFundingRequestOptions(
  catalogue: FundingRequestOption[],
  existingCosts: IFdFundingRequestCost[],
): FundingRequestOption[] {
  const customCosts = existingCosts.filter((cost) => {
    if (['seed', 'fertiliser', 'chemical'].includes(String(cost.id))) {
      return false;
    }

    return !catalogue.some((item) => item.id === String(cost.id));
  });

  const merged: FundingRequestOption[] = [...catalogue];
  customCosts.forEach((cost) => {
    merged.push({
      id: String(cost.id),
      label: String(cost.label || 'Funding item'),
      cost: parseCurrency(cost.cost),
      source: 'custom',
    });
  });

  const deduped = merged.reduce<FundingRequestOption[]>((acc, current) => {
    const existingIndex = acc.findIndex((item) => item.id === current.id);

    if (existingIndex === -1) {
      acc.push(current);
    } else if (current.cost > acc[existingIndex].cost) {
      acc[existingIndex] = current;
    }

    return acc;
  }, []);

  return deduped.filter((item) => item.cost > 0);
}

const ApplicationFinancialsFundingRequestStep: React.FC<
  ApplicationFinancialsFundingRequestStepProps
> = ({applicationId, application, onRegisterController, onComplete}) => {
  const [options, setOptions] = useState<FundingRequestOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [expectedMonth, setExpectedMonth] = useState('');
  const [expectedYear, setExpectedYear] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const list: Array<{value: string; label: string}> = [];
    for (let year = currentYear; year <= currentYear + 5; year += 1) {
      list.push({value: String(year), label: String(year)});
    }
    return list;
  }, [currentYear]);

  const coreOptions = useMemo(
    () => buildCoreOptions(application?.productionCosts),
    [application?.productionCosts],
  );

  const operatingExpenseOptions = useMemo(
    () => buildOperatingExpenseOptions(application?.operatingCosts),
    [application?.operatingCosts],
  );

  const setStatusOnce = useCallback((message: string) => {
    setStatusMessage(message);
    window.setTimeout(() => setStatusMessage(''), 4000);
  }, []);

  const loadFundingRequest = useCallback(async () => {
    if (!Number.isFinite(applicationId)) return;

    setIsLoading(true);
    const catalogue = [...coreOptions, ...operatingExpenseOptions];

    try {
      const record =
        (await apiGetFdFundingAsksByApplicationId(applicationId)) ||
        application?.fundingAsk;

      const existingCosts = record?.fundingRequestCosts || [];
      const mergedOptions = mergeFundingRequestOptions(
        catalogue,
        existingCosts,
      );

      setOptions(mergedOptions);
      setSelectedIds(
        existingCosts
          .map((item) => String(item.id))
          .filter((id) => mergedOptions.some((option) => option.id === id)),
      );
      setNotes(String(record?.notes || ''));
      setExpectedMonth(
        record?.expectedFundingMonth ? String(record.expectedFundingMonth) : '',
      );
      setExpectedYear(
        record?.expectedFundingYear ? String(record.expectedFundingYear) : '',
      );

      if (mergedOptions.length === 0) {
        setStatusMessage(
          'No eligible costs found yet. Add production costs or operating expenses first.',
        );
      } else {
        setStatusMessage('');
      }
    } catch (error) {
      console.error('Failed to load funding request', error);

      const record = application?.fundingAsk as IFdFundingAsk | undefined;
      const existingCosts = record?.fundingRequestCosts || [];
      const mergedOptions = mergeFundingRequestOptions(
        catalogue,
        existingCosts,
      );

      setOptions(mergedOptions);
      setSelectedIds(
        existingCosts
          .map((item) => String(item.id))
          .filter((id) => mergedOptions.some((option) => option.id === id)),
      );
      setNotes(String(record?.notes || ''));
      setExpectedMonth(
        record?.expectedFundingMonth ? String(record.expectedFundingMonth) : '',
      );
      setExpectedYear(
        record?.expectedFundingYear ? String(record.expectedFundingYear) : '',
      );
      setStatusMessage('Failed to load funding request.');
    } finally {
      setIsLoading(false);
    }
  }, [
    application?.fundingAsk,
    applicationId,
    coreOptions,
    operatingExpenseOptions,
  ]);

  useEffect(() => {
    void loadFundingRequest();
  }, [loadFundingRequest]);

  const totalSelected = useMemo(
    () =>
      selectedIds.reduce((sum, id) => {
        const item = options.find((option) => option.id === id);
        return item ? sum + item.cost : sum;
      }, 0),
    [options, selectedIds],
  );

  const canSubmit =
    selectedIds.length > 0 &&
    Boolean(expectedMonth) &&
    Boolean(expectedYear) &&
    !isLoading;

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    if (!Number.isFinite(applicationId)) {
      setStatusOnce('We could not find this application.');
      window.alert(
        'We could not find this application. Please refresh and try again.',
      );
      return;
    }

    if (!expectedMonth || !expectedYear) {
      setStatusOnce('Select the expected funding month and year.');
      window.alert('Select the expected funding month and year.');
      return;
    }

    const selectedCosts = selectedIds
      .map((id) => options.find((option) => option.id === id))
      .filter((item): item is FundingRequestOption => Boolean(item))
      .filter((item) => item.cost > 0);

    if (selectedCosts.length === 0) {
      setStatusOnce('Select at least one cost to include in the funding ask.');
      window.alert('Select at least one cost to include in the funding ask.');
      return;
    }

    setIsSaving(true);

    try {
      await apiCreateFdFundingAsk({
        applicationId,
        fundingRequestCosts: selectedCosts.map((item) => ({
          id: item.id,
          label: item.label,
          cost: item.cost,
        })),
        expectedFundingYear: Number(expectedYear),
        expectedFundingMonth: Number(expectedMonth),
        notes,
      });

      setStatusMessage('Funding request saved successfully.');
      await onComplete();
    } catch (error) {
      console.error('Failed to save funding request', error);
      setStatusOnce('Failed to save funding request.');
      window.alert(
        'We could not submit the funding ask right now. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    applicationId,
    expectedMonth,
    expectedYear,
    isSaving,
    notes,
    onComplete,
    options,
    selectedIds,
    setStatusOnce,
  ]);

  useEffect(() => {
    onRegisterController({
      canSubmit,
      isLoading,
      isSaving,
      save: handleSave,
    });
  }, [canSubmit, handleSave, isLoading, isSaving, onRegisterController]);

  return (
    <div className="financials-funding-request">
      <div className="sr-only" role="status" aria-live="polite">
        {statusMessage}
      </div>

      {isLoading ? (
        <div className="financials-funding-request__loading" role="status">
          <FontAwesomeIcon icon={iconSpinner} spin />
          <span>Loading funding request...</span>
        </div>
      ) : null}

      {!isLoading && options.length === 0 ? (
        <div className="application-card">
          <div className="application-empty-state">
            <h3>Add costs to your funding request</h3>
            <p>
              Capture production costs, operating expenses or custom items to
              request funding.
            </p>
          </div>
        </div>
      ) : null}

      <div className="financials-funding-request__grid" aria-live="polite">
        {options.map((option) => {
          const isSelected = selectedIds.includes(option.id);

          return (
            <button
              key={option.id}
              type="button"
              className={`financials-funding-request__chip financials-funding-request__chip--${
                option.source
              }${isSelected ? ' is-selected' : ''}`}
              onClick={() => toggleSelection(option.id)}
            >
              <span className="financials-funding-request__chip-label">
                {option.label}
              </span>
              <span className="financials-funding-request__chip-cost">
                {toCurrency(option.cost)}
              </span>
              {isSelected ? (
                <FontAwesomeIcon icon={iconCheck} aria-hidden="true" />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="financials-funding-request__summary">
        <div>
          <p className="financials-funding-request__summary-label">
            Selected costs total
          </p>
          <p className="financials-funding-request__summary-value">
            {toCurrency(totalSelected)}
          </p>
        </div>

        <div className="financials-funding-request__fields">
          <label className="form-field">
            <span>Expected funding month</span>
            <select
              value={expectedMonth}
              onChange={(event) => setExpectedMonth(event.target.value)}
            >
              <option value="">Select month</option>
              {MONTH_OPTIONS.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </label>

          <label className="form-field">
            <span>Expected funding year</span>
            <select
              value={expectedYear}
              onChange={(event) => setExpectedYear(event.target.value)}
            >
              <option value="">Select year</option>
              {yearOptions.map((year) => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <label className="form-field" htmlFor="financials-funding-request-notes">
        <span>Notes for the Khula team (optional)</span>
        <textarea
          id="financials-funding-request-notes"
          rows={4}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>
    </div>
  );
};

export default ApplicationFinancialsFundingRequestStep;
